import React, { useMemo } from 'react';

export type MapRoute = {
  color: string;
  pts: [number, number][];
  live?: boolean;
  label: string;
};

type FleetMapProps = {
  height?: number;
  dark?: boolean;
  routes?: MapRoute[];
};

export function FleetMap({ height = 460, dark = true, routes: routesProp }: FleetMapProps) {
  const bg = dark ? '#0E1A2B' : '#EFEDE7';
  const land = dark ? '#15263C' : '#E4E3DE';
  const land2 = dark ? '#11202F' : '#E4E3DE';
  const roadC = dark ? '#243B57' : '#D6D3CC';

  const routes = routesProp?.length
    ? routesProp
    : [
        { color: '#00707F', pts: [[60, 360], [150, 330], [240, 300], [330, 250], [430, 230], [520, 180]] as [number, number][], live: true, label: 'LIVE' },
      ];

  const mk = (pts: [number, number][]) => pts.map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1]).join(' ');

  return (
    <svg width="100%" height={height} viewBox="0 0 880 480" preserveAspectRatio="xMidYMid slice" style={{ display: 'block' }}>
      <rect width="880" height="480" fill={bg} />
      <path d="M0 0 H260 L180 120 L0 180 Z" fill={land} />
      <path d="M880 480 H560 L680 340 L880 300 Z" fill={land} />
      <rect x="0" y="240" width="150" height="200" rx="14" fill={land2} />
      <rect x="600" y="0" width="280" height="150" rx="14" fill={land2} />
      <circle cx="360" cy="400" r="70" fill={land2} />
      <circle cx="470" cy="120" r="48" fill={land2} />
      <path d="M-20 280 C 160 280, 300 200, 460 210 S 760 250, 900 220" stroke={roadC} strokeWidth="16" fill="none" strokeLinecap="round" />
      <path d="M160 -20 C 160 120, 230 220, 210 340 S 250 500, 250 520" stroke={roadC} strokeWidth="13" fill="none" strokeLinecap="round" />
      <path d="M560 -20 C 540 120, 600 220, 560 340 S 520 500, 540 520" stroke={roadC} strokeWidth="12" fill="none" strokeLinecap="round" />
      <path d="M-20 150 C 200 150, 420 90, 620 110 S 880 140, 920 120" stroke={roadC} strokeWidth="11" fill="none" strokeLinecap="round" />
      {routes.map((r, i) => {
        const head = r.pts[r.pts.length - 1];
        const start = r.pts[0];
        return (
          <g key={i}>
            <path d={mk(r.pts)} stroke={r.color} strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
            <circle cx={start[0]} cy={start[1]} r="6" fill={dark ? '#0E1A2B' : '#fff'} stroke={r.color} strokeWidth="3" />
            {r.live !== false && (
              <g transform={`translate(${head[0]} ${head[1]})`}>
                <circle r="18" fill={r.color} opacity="0.18">
                  <animate attributeName="r" values="12;24;12" dur="2.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.3;0;0.3" dur="2.6s" repeatCount="indefinite" />
                </circle>
                <circle r="14" fill={r.color} />
                <circle r="11" fill="#fff" />
                <g transform="translate(-6.5 -6.5) scale(0.55)">
                  <path d="M1 3h15v13H1z M16 8h4l3 3v5h-7 M5.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M18.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" stroke={r.color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </g>
              </g>
            )}
            <g transform={`translate(${head[0] + 18} ${head[1] - 26})`}>
              <rect x="0" y="0" width={r.label.length * 7.2 + 16} height="22" rx="6" fill={dark ? '#0F1419' : '#fff'} stroke={dark ? 'rgba(255,255,255,0.12)' : '#E4E3DE'} />
              <text x="8" y="15" fill={dark ? '#fff' : '#0F1419'} fontSize="11" fontFamily="JetBrains Mono, monospace" fontWeight="600">{r.label}</text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

type CargoMapProps = {
  height?: number;
  progress?: number;
  animated?: boolean;
  dark?: boolean;
};

export function CargoMap({ height = 360, progress = 0.62, animated = true, dark = false }: CargoMapProps) {
  const bg = dark ? '#0F1419' : '#EFEDE7';
  const land = dark ? '#16263C' : '#E4E3DE';
  const land2 = dark ? '#13202F' : '#E4E3DE';
  const road = dark ? '#2C3E57' : '#D6D3CC';

  const pts: [number, number][] = [
    [34, 300], [78, 250], [120, 232], [168, 190], [214, 168], [250, 120], [286, 74],
  ];

  const { path, total, mx, my } = useMemo(() => {
    const pathStr = pts.map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1]).join(' ');
    const segLens: number[] = [];
    let totalLen = 0;
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i][0] - pts[i - 1][0];
      const dy = pts[i][1] - pts[i - 1][1];
      const l = Math.hypot(dx, dy);
      segLens.push(l);
      totalLen += l;
    }
    let target = totalLen * progress;
    let acc = 0;
    let mxVal = pts[0][0];
    let myVal = pts[0][1];
    for (let i = 0; i < segLens.length; i++) {
      if (acc + segLens[i] >= target) {
        const t = (target - acc) / segLens[i];
        mxVal = pts[i][0] + (pts[i + 1][0] - pts[i][0]) * t;
        myVal = pts[i][1] + (pts[i + 1][1] - pts[i][1]) * t;
        break;
      }
      acc += segLens[i];
    }
    return { path: pathStr, total: totalLen, mx: mxVal, my: myVal };
  }, [progress]);

  return (
    <svg width="100%" height={height} viewBox="0 0 320 360" preserveAspectRatio="xMidYMid slice" style={{ display: 'block' }}>
      <rect width="320" height="360" fill={bg} />
      <path d="M0 0 H150 L100 70 L0 110 Z" fill={land} />
      <path d="M320 360 H180 L240 250 L320 230 Z" fill={land} />
      <rect x="0" y="150" width="90" height="120" rx="10" fill={land2} />
      <rect x="210" y="0" width="110" height="110" rx="10" fill={land2} />
      <circle cx="150" cy="300" r="46" fill={land2} />
      <path d="M-10 200 C 80 200, 140 150, 200 150 S 320 170, 340 150" stroke={road} strokeWidth="12" fill="none" strokeLinecap="round" />
      <path d="M60 -10 C 60 60, 100 120, 90 200 S 120 320, 120 380" stroke={road} strokeWidth="10" fill="none" strokeLinecap="round" />
      <path d="M250 -10 C 240 60, 270 120, 250 200 S 230 320, 240 380" stroke={road} strokeWidth="9" fill="none" strokeLinecap="round" />
      <path d={path} stroke={dark ? '#3A5273' : '#A9C2DE'} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 8" />
      <path d={path} stroke="#00707F" strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={total} strokeDashoffset={total * (1 - progress)} />
      <circle cx={pts[0][0]} cy={pts[0][1]} r="6.5" fill={dark ? '#0F1419' : '#fff'} stroke="#00707F" strokeWidth="3" />
      <g transform={`translate(${pts[pts.length - 1][0]} ${pts[pts.length - 1][1]})`}>
        <circle r="9" fill="#1E7A4C" stroke={dark ? '#0F1419' : '#fff'} strokeWidth="3" />
        <path d="M-2 -4 v8 M-2 -4 h5 l-1.5 2 1.5 2 h-5" fill="#fff" stroke="#fff" strokeWidth="0.6" transform="scale(0.8)" />
      </g>
      {pts.slice(1, -1).map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={dark ? '#0F1419' : '#fff'} stroke="#00707F" strokeWidth="2" />
      ))}
      <g transform={`translate(${mx} ${my})`}>
        {animated && (
          <circle r="20" fill="#00707F" opacity="0.18">
            <animate attributeName="r" values="13;26;13" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.32;0;0.32" dur="2.4s" repeatCount="indefinite" />
          </circle>
        )}
        <circle r="15" fill="#00707F" />
        <circle r="12" fill="#fff" />
        <g transform="translate(-7 -7) scale(0.6)">
          <path d="M1 3h15v13H1z M16 8h4l3 3v5h-7 M5.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M18.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" stroke="#00707F" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
    </svg>
  );
}
